import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useGoBack } from '@/hooks/useGoBack';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DELETE_INVESTMENT_MUTATION } from '../../queries';

interface DeleteStrategyProps {
  isOpen: boolean;
  strategy: {
    id: string;
    name: string;
  };
  onClose: () => void;
}

const DeleteStrategy = ({ isOpen, strategy, onClose }: DeleteStrategyProps) => {
  const goBack = useGoBack();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { searchParams } = useSearchParamsManager();

  const isNewStrategy = searchParams.get('isNewStrategy') === 'true';

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [deleteStrategy, { loading: deleteStrategyLoading }] = useMutation(DELETE_INVESTMENT_MUTATION);

  const handleDeleteStrategy = async () => {
    try {
      setErrorMessage(null);

      await deleteStrategy({
        variables: {
          id: strategy.id
        }
      });

      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  const handleCloseSuccessDialog = () => {
    goBack({ fallBack: '/dealPipeline', goBackSteps: isNewStrategy ? 2 : 1 });
  };

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={handleCloseSuccessDialog}
        confirmButton={{ name: 'OK', onClick: handleCloseSuccessDialog }}
        showCancelButton={false}
      >
        <ModalBody>
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>Success</ModalTitle>
          <ModalDescription>
            <b>{strategy.name}</b> has been deleted.
          </ModalDescription>
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      cancelButtonName="No, Cancel and Do Not Delete"
      confirmButton={{
        name: 'Yes, Delete Forever',
        onClick: handleDeleteStrategy,
        disabled: deleteStrategyLoading
      }}
      showCloseIcon={!deleteStrategyLoading}
      showCancelButton={!deleteStrategyLoading}
      isUseOutsideClick={!deleteStrategyLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteStrategyLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this Strategy</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>
              This action will delete <b>{strategy.name}</b> and all associated data. This action cannot be undone. Are you absolutely sure that you
              want to proceed?
            </ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteStrategy;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
